/**
 * `/__ui/docs` — the visual-regression harness for M11, and the surface
 * `tools/browser/verify-export-docs-local.mjs` hit-tests.
 *
 * It renders with **no session, no api and no database**, from a fixture whose block shapes
 * are copied out of the account's own snapshot (`discovery/raw/snapshot-2026-09-10/
 * doc-blocks.json`): one of every one of the thirteen types that actually occur, including the
 * nested `table` → `cell` → `normal text` chain that closes q407.
 *
 *   `?screen=doc` (default) · `slash` · `export` · `template` · `picker`
 */
import { useState } from 'react';
import { useOverlayDismiss } from '@elizmonday/ui';
import { buildDocBlockTree, type DocBlockContent, type DocBlockRow } from '@elizmonday/shared';
import { DocBlocks } from './blocks';
import { SlashMenu } from './SlashMenu';
import './docs.css';
import '../export/export.css';
import '../templates/templates.css';

const delta = (text: string): DocBlockContent => ({
  alignment: 'left',
  direction: 'ltr',
  deltaFormat: [{ insert: text }],
});

/**
 * Every block type present in the 20 imported docs, with the content shapes they really carry.
 * `position` is our ordering string, not monday's float — the importer re-mints them.
 */
export const FIXTURE_BLOCKS: DocBlockRow[] = [
  { id: 'b-h1', type: 'large title', content: delta('Eli Z operations'), position: '0000000001', parentBlockId: null },
  {
    id: 'b-p1',
    type: 'normal text',
    content: {
      alignment: 'left',
      direction: 'ltr',
      deltaFormat: [
        { insert: 'ACTUALLY DEACTIVATED', attributes: { bold: true } },
        { insert: '. The manual price is updated by a script and ' },
        { insert: 'linked here', attributes: { link: 'https://example.com/manual-price', underline: true } },
        { insert: '.' },
      ],
    } as DocBlockContent,
    position: '0000000002',
    parentBlockId: null,
  },
  { id: 'b-h2', type: 'medium title', content: delta('Monday'), position: '0000000003', parentBlockId: null },
  { id: 'b-h3', type: 'small title', content: delta('Weekly checks'), position: '0000000004', parentBlockId: null },
  { id: 'b-c1', type: 'check list', content: { ...delta('Review the open channels'), checked: true }, position: '0000000005', parentBlockId: null },
  { id: 'b-c2', type: 'check list', content: { ...delta('Archive anything idle for three months'), checked: false }, position: '0000000006', parentBlockId: null },
  { id: 'b-b1', type: 'bulleted list', content: delta('each night'), position: '0000000007', parentBlockId: null },
  { id: 'b-b2', type: 'bulleted list', content: { ...delta('nested once'), indentation: 1 }, position: '0000000008', parentBlockId: null },
  { id: 'b-n1', type: 'numbered list', content: delta('Budget: 50$, spent at 8pm 40$ → Ratio is 80%'), position: '0000000009', parentBlockId: null },
  { id: 'b-n2', type: 'numbered list', content: { ...delta('Second step'), indentation: 1 }, position: '0000000010', parentBlockId: null },
  { id: 'b-d1', type: 'divider', content: { alignment: 'left', direction: 'ltr' } as DocBlockContent, position: '0000000011', parentBlockId: null },
  {
    id: 'b-img',
    type: 'image',
    content: {
      alignment: 'left',
      direction: 'ltr',
      // A 1×1 transparent gif: the harness must render with no network at all.
      publicUrl: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
      widthPercentage: 30,
    } as DocBlockContent,
    position: '0000000012',
    parentBlockId: null,
  },
  { id: 'b-file', type: 'file', content: { alignment: 'left', direction: 'ltr', url: 'spec.pdf' } as DocBlockContent, position: '0000000013', parentBlockId: null },
  {
    id: 'b-board',
    type: 'board',
    content: { type: 'board', width: 1026, height: 436, widgetId: 89064240, alignment: 'left', direction: 'ltr' } as DocBlockContent,
    position: '0000000014',
    parentBlockId: null,
  },
  {
    id: 'b-table',
    type: 'table',
    content: {
      cells: [
        [{ blockId: 'b-cell-1' }, { blockId: 'b-cell-2' }],
        [{ blockId: 'b-cell-3' }, { blockId: 'b-cell-4' }],
      ],
      columnsStyle: {},
    } as DocBlockContent,
    position: '0000000015',
    parentBlockId: null,
  },
  { id: 'b-cell-1', type: 'cell', content: { alignment: 'left', direction: 'ltr' } as DocBlockContent, position: '0000000016', parentBlockId: 'b-table' },
  { id: 'b-cell-2', type: 'cell', content: { alignment: 'left', direction: 'ltr' } as DocBlockContent, position: '0000000017', parentBlockId: 'b-table' },
  { id: 'b-cell-3', type: 'cell', content: { alignment: 'left', direction: 'ltr' } as DocBlockContent, position: '0000000018', parentBlockId: 'b-table' },
  { id: 'b-cell-4', type: 'cell', content: { alignment: 'left', direction: 'ltr' } as DocBlockContent, position: '0000000019', parentBlockId: 'b-table' },
  { id: 'b-tc-1', type: 'normal text', content: delta('a'), position: '0000000020', parentBlockId: 'b-cell-1' },
  { id: 'b-tc-2', type: 'normal text', content: delta('b'), position: '0000000021', parentBlockId: 'b-cell-2' },
  { id: 'b-tc-3', type: 'normal text', content: delta('1'), position: '0000000022', parentBlockId: 'b-cell-3' },
  { id: 'b-tc-4', type: 'normal text', content: delta('2'), position: '0000000023', parentBlockId: 'b-cell-4' },
];

/**
 * B9's two dialogs, rendered **statically**. The real ones are tRPC-backed, and the harness
 * must run with no api at all — so the verifier hit-tests this markup, which is the same DOM
 * and the same stylesheet, and the e2e suite hit-tests the live ones against the database.
 */
function ExportDialogFixture() {
  return (
    <div className="ez-dialog-backdrop" role="presentation">
      <div className="ez-export-dialog" role="dialog" aria-modal="true" aria-label="Export to Excel" data-testid="export-dialog">
        <header className="ez-export-dialog__head">
          <h2>Export to Excel</h2>
          <button type="button" aria-label="Close" data-testid="export-close">
            ✕
          </button>
        </header>
        <p className="ez-export-dialog__section">What to include?</p>
        <label className="ez-export-dialog__check">
          <input type="checkbox" data-testid="export-updates" defaultChecked={false} />
          <span>Updates</span>
        </label>
        <label className="ez-export-dialog__check">
          <input type="checkbox" data-testid="export-subitems" defaultChecked />
          <span>Subitems</span>
        </label>
        <div className="ez-export-dialog__email">
          <div>
            <strong>Email me a copy</strong>
            <em>The download link remains active for 60 minutes.</em>
          </div>
          <span className="ez-export-dialog__toggle">
            <span>No</span>
            <button type="button" role="switch" aria-checked={false} data-testid="export-email" disabled />
            <span>Yes</span>
          </span>
        </div>
        <p className="ez-export-dialog__note" data-testid="export-note">
          Your export will include applied Filters. Other display settings (e.g Sort, Group by..) are
          not supported.
        </p>
        <footer className="ez-export-dialog__foot">
          <button type="button" className="ez-export-dialog__submit" data-testid="export-submit">
            Export
          </button>
        </footer>
      </div>
    </div>
  );
}

function SaveTemplateFixture() {
  /*
   * P-013: a fixture copy of `SaveAsTemplateDialog`'s markup (the real one needs a tRPC client
   * and a board). It gets the real dialog's Escape contract so the sweep measures the same
   * thing here as it does in the product.
   */
  const [closed, setClosed] = useState(false);
  useOverlayDismiss({ open: !closed, onClose: () => setClosed(true) });
  if (closed) return null;
  return (
    <div className="ez-dialog-backdrop" role="presentation">
      <form className="ez-template-dialog" role="dialog" aria-modal="true" aria-label="Save as new template" data-testid="save-template-dialog">
        <div className="ez-template-dialog__form">
          <h2>Save as new template</h2>
          <label className="ez-template-dialog__field">
            Name
            <input data-testid="save-template-name" defaultValue="Eli Z operations template" />
          </label>
          <fieldset className="ez-template-dialog__radios">
            <legend>Template privacy</legend>
            <label>
              <input type="radio" name="k" data-testid="save-template-public" defaultChecked />
              <span>Public</span>
            </label>
            <label>
              <input type="radio" name="k" data-testid="save-template-private" />
              <span>🔒 Private</span>
            </label>
          </fieldset>
          <p className="ez-template-dialog__hint">
            Anyone can use this template. only invited people and admins can edit it.
          </p>
          <div className="ez-template-dialog__upsell" data-testid="save-template-managed">
            <strong>Create managed template</strong>
            <span>
              Editing managed templates will update all copies created from it. ideal for syncing
              multiple projects at once.
            </span>
          </div>
        </div>
        <div className="ez-template-dialog__preview" aria-hidden="true" />
        <footer className="ez-template-dialog__foot">
          <button type="button" className="ez-template-dialog__cancel">
            Cancel
          </button>
          <button type="button" className="ez-template-dialog__save" data-testid="save-template-submit">
            Save template
          </button>
        </footer>
      </form>
    </div>
  );
}

export function DocsHarness() {
  const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);
  const screen = params.get('screen') ?? 'doc';
  const [blocks, setBlocks] = useState<DocBlockRow[]>(FIXTURE_BLOCKS);
  /* P-013: the `/` menu's own `onClose` has to do something, or Escape cannot be measured. */
  const [slashClosed, setSlashClosed] = useState(false);
  const nodes = buildDocBlockTree(blocks);

  if (screen === 'export') {
    return (
      <div data-testid="ui-docs-export" style={{ minHeight: '100vh' }}>
        <ExportDialogFixture />
      </div>
    );
  }
  if (screen === 'template') {
    return (
      <div data-testid="ui-docs-template" style={{ minHeight: '100vh' }}>
        <SaveTemplateFixture />
      </div>
    );
  }

  return (
    <div data-testid={`ui-docs-${screen}`} style={{ minHeight: '100vh' }}>
      <div className="ez-doc" data-testid="doc-page">
        <header className="ez-doc__toolbar" data-testid="doc-toolbar">
          <button type="button" className="ez-doc__add" data-testid="doc-add">
            + Add
          </button>
          <span className="ez-doc__divider" />
          <span className="ez-doc__blocktype">Normal text</span>
          <span className="ez-doc__divider" />
          <button type="button" className="ez-doc__tool" disabled title="not built">
            Style
          </button>
        </header>
        <div className="ez-doc__column">
          <h1 className="ez-doc__title" data-testid="doc-title">
            Eli Z operations
          </h1>
          <p className="ez-doc__meta" data-testid="doc-meta">
            <span>
              Creator <strong>Roberto</strong>
            </span>
            <span>
              Created <strong>24 Jan 2022</strong>
            </span>
            <span>
              Last updated <strong>11 Sep 2026</strong>
            </span>
          </p>
          <div className="ez-doc__body" data-testid="doc-body">
            <DocBlocks
              nodes={nodes}
              onToggleCheck={(node, checked) =>
                setBlocks((current) =>
                  current.map((block) =>
                    block.id === node.id ? { ...block, content: { ...block.content, checked } } : block,
                  ),
                )
              }
            />
          </div>
        </div>
        {screen === 'slash' && !slashClosed ? (
          <SlashMenu x={520} y={320} onPick={() => undefined} onClose={() => setSlashClosed(true)} />
        ) : null}
      </div>
    </div>
  );
}
